import {
  Button,
  Card,
  ColorPicker,
  Input,
  message,
  Space,
  Tag,
  Typography,
} from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { API_URL } from '../../services/api/constants';
import { apiGenerateOptionIcon } from '../../services/api/images';
import { selectUserState } from '../../store/selectors/user';

const { TextArea } = Input;
const { Title } = Typography;

export default function FormOptionIconGenerator() {
  const { token } = useSelector(selectUserState);
  const [loading, setLoading] = useState(false);
  const [formTemplate, setFormTemplate] = useState('');
  const [formQuestion, setFormQuestion] = useState('');
  const [labelsInput, setLabelsInput] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [accentColor, setAccentColor] = useState('#016DF9');

  const handleAddLabel = () => {
    const trimmedLabel = labelsInput.trim();
    if (trimmedLabel && !labels.includes(trimmedLabel)) {
      setLabels([...labels, trimmedLabel]);
      setLabelsInput('');
    }
  };

  const handleRemoveLabel = (labelToRemove: string) => {
    setLabels(labels.filter((label) => label !== labelToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLabel();
    }
  };

  const isFormValid = () => {
    return (
      formTemplate.trim() !== '' &&
      formQuestion.trim() !== '' &&
      labels.length > 0 &&
      !loading
    );
  };

  const downloadImage = async (imageId: number, filename: string) => {
    try {
      const response = await fetch(
        `${API_URL}/api/images/download?imageId=${imageId}`,
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      message.error(`Failed to download ${filename}`);
    }
  };

  const handleGenerate = async () => {
    if (!isFormValid() || !token) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const label of labels) {
      try {
        const result = await apiGenerateOptionIcon(
          formTemplate,
          formQuestion,
          label,
          accentColor,
          token,
        );

        if (result?.data) {
          await downloadImage(result.data.id, result.data.filename);
          successCount++;
        } else {
          failCount++;
          message.error(`Failed to generate icon for "${label}"`);
        }
      } catch (error) {
        failCount++;
        console.error(`Error generating icon for "${label}":`, error);
        message.error(`Error generating icon for "${label}"`);
      }

      // Small delay between requests to avoid overwhelming the server
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setLoading(false);

    if (successCount > 0) {
      message.success(
        `Successfully generated ${successCount} icon${
          successCount > 1 ? 's' : ''
        }!`,
      );
    }
    if (failCount > 0) {
      message.warning(
        `Failed to generate ${failCount} icon${failCount > 1 ? 's' : ''}`,
      );
    }
  };

  return (
    <Card bordered={false} title="Form Option Icon Generator">
      <div style={{ maxWidth: 800 }}>
        <Title level={2}>Generate Option Icons with AI</Title>
        <Typography.Paragraph>
          Generate custom icons for form template single select options using
          AI. Provide the form context and option labels, and the system will
          create unique icons for each option.
        </Typography.Paragraph>

        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            Form Template Name
          </label>
          <TextArea
            rows={2}
            placeholder="e.g., Lead Generation Form, Survey Template, etc."
            value={formTemplate}
            onChange={(e) => {
              setFormTemplate(e.target.value);
            }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            Form Question
          </label>
          <Input
            placeholder="e.g., What is your budget range?"
            value={formQuestion}
            onChange={(e) => {
              setFormQuestion(e.target.value);
            }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            Option Labels
          </label>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Enter a label and press Enter or click Add"
                value={labelsInput}
                onChange={(e) => {
                  setLabelsInput(e.target.value);
                }}
                onKeyPress={handleKeyPress}
              />
              <Button onClick={handleAddLabel}>Add</Button>
            </Space.Compact>
            {labels.length > 0 && (
              <div>
                {labels.map((label) => (
                  <Tag
                    key={label}
                    closable
                    onClose={() => {
                      handleRemoveLabel(label);
                    }}
                    style={{ marginBottom: 8 }}
                  >
                    {label}
                  </Tag>
                ))}
              </div>
            )}
          </Space>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            Accent Color
          </label>
          <ColorPicker
            value={accentColor}
            onChange={(color) => {
              setAccentColor(color.toHexString());
            }}
            showText
            format="hex"
          />
        </div>

        <Button
          type="primary"
          size="large"
          onClick={handleGenerate}
          loading={loading}
          disabled={!isFormValid()}
        >
          Generate Icons ({labels.length})
        </Button>

        {loading && (
          <Typography.Paragraph style={{ marginTop: 16 }}>
            Generating icons... This may take a moment.
          </Typography.Paragraph>
        )}
      </div>
    </Card>
  );
}
